/**
 * Feedback component overrides: alerts, dialogs, snackbars, tooltips.
 * @see https://mui.com/material-ui/components/alert/
 * @see https://mui.com/material-ui/components/dialog/
 * @see https://mui.com/material-ui/components/snackbar/
 * @see https://mui.com/material-ui/components/tooltip/
 */
import varnishTokens from '@allenai/varnish-theme/mui';
import type { Components, Theme } from '@mui/material';

export const feedbackComponents: Components<Omit<Theme, 'components'>> = {
    MuiAlert: {
        styleOverrides: {
            root: {
                borderRadius: varnishTokens.radii.sm.value,
                padding: '8px',
                fontSize: varnishTokens.font.size.sm.value,
                gap: '4px',
            },
            icon: {
                fontSize: '22px',
                padding: 0,
                marginRight: 0,
            },
            message: { padding: 0 },
        },
        // MUI's filled Alert ignores palette.*.contrastText and re-computes via
        // getContrastText(). Override here to match varnish-theme element contrast tokens.
        variants: [
            {
                props: { variant: 'filled', severity: 'info' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        color: palette.info.contrastText,
                        '& .MuiAlert-icon': { color: palette.info.contrastText },
                    };
                },
            },
            {
                props: { variant: 'filled', severity: 'success' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        color: palette.success.contrastText,
                        '& .MuiAlert-icon': { color: palette.success.contrastText },
                    };
                },
            },
            {
                props: { variant: 'filled', severity: 'warning' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        color: palette.warning.contrastText,
                        '& .MuiAlert-icon': { color: palette.warning.contrastText },
                    };
                },
            },
            {
                props: { variant: 'filled', severity: 'error' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        color: palette.error.contrastText,
                        '& .MuiAlert-icon': { color: palette.error.contrastText },
                    };
                },
            },
        ],
    },
    MuiDialogActions: {
        styleOverrides: {
            root: {
                padding: `0 ${varnishTokens.spacing[2].value} ${varnishTokens.spacing[2].value}`,
            },
        },
    },
    MuiDialogContent: {
        styleOverrides: {
            root: {
                padding: `0 ${varnishTokens.spacing[2].value} ${varnishTokens.spacing[2].value}`,
            },
        },
    },
    MuiDialogTitle: {
        styleOverrides: {
            root: {
                padding: varnishTokens.spacing[2].value,
            },
        },
    },
    MuiSnackbarContent: {
        styleOverrides: {
            root: ({ theme }) => {
                const { palette } = theme.vars ?? theme;
                return {
                    backgroundColor: palette.background.reversed,
                    color: palette.text.reversed,
                };
            },
            message: {
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
            },
            action: {
                marginLeft: 'auto',
                gap: varnishTokens.spacing[1].value,
            },
        },
        variants: [
            {
                props: { shape: 'rounded' },
                style: { borderRadius: varnishTokens.radii.full.value },
            },
        ],
    },
    MuiTooltip: {
        defaultProps: {
            shape: 'box',
        },
        styleOverrides: {
            tooltip: ({ theme, ownerState }) => {
                const { palette } = theme.vars ?? theme;
                return {
                    backgroundColor: palette.background.reversed,
                    color: palette.text.reversed,
                    padding: '6px 12px',
                    fontSize: varnishTokens.font.size.sm.value,
                    borderRadius: varnishTokens.radii.sm.value,
                    ...(ownerState.shape === 'rounded' && {
                        borderRadius: varnishTokens.radii.full.value,
                        padding: '6px 18px',
                    }),
                };
            },
            arrow: ({ theme }) => ({
                color: (theme.vars ?? theme).palette.background.reversed,
            }),
        },
    },
};
